// Enums

export enum LoanType {
  Commercial = 0,
  Personal = 1
}

export enum Coverage {
  Aircraft = 0
}

export enum CancelDays {
  D10 = 10,
  D15 = 15,
  D20 = 20,
  D25 = 25,
  D30 = 30,
  D35 = 35,
  D45 = 45,
  D60 = 60
}

export enum QuoteStatus {
  Quoted = 0,
  Complete = 1,
  Delinquent = 2,
  InProgress = 3,
  Grace = 4,
  Cancelled = 5
}

export enum PaymentType {
  Unknown = 0,
  DownPayment = 1,
  MonthlyPayment = 2,
  LateFee = 3,
  Payoff = 4
}

// Policy related interfaces
export interface PolicyDto {
  policyNumber: string;
  coverage: Coverage;
  effectiveDate: Date;
  expirationDate?: Date;
  premiumAmount: number;
  taxesAmount: number;
  feesAmount: number;
  minEarnedPercent?: number;
  minEarnedAmount?: number;
  downAmount?: number;
  cancelDays: CancelDays;
  auditable: boolean;
  lossPayeeRequired: boolean;
  lossPayeeRequest: boolean;
}

export interface Policy {
  id: number;
  quoteId: number;
  policyNumber: string;
  coverage: Coverage;
  effectiveDate: Date;
  expirationDate: Date;
  premiumAmount: number;
  taxesAmount: number;
  feesAmount: number;
  totalPremium: number;
  minEarnedPercent: number;
  minEarnedAmount: number;
  downAmount: number;
  cancelDays: CancelDays;
  auditable: boolean;
  lossPayeeRequired: boolean;
  lossPayeeRequest: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Quote interfaces
export interface Quote {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  agentName: string;
  insuredName: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  customerNumber?: string;
  loanType: LoanType;
  status: QuoteStatus;
  paymentMethodType?: PaymentMethodType;
  stripeSubscriptionId?: string;
  quoteGuid: string;
  programId: number;
  programName?: string;
  program?: FinanceProgram;
  insuranceCompanyId?: number;
  insuranceCompany?: InsuranceCompany;
  createdByProducerId: number;
  createdByProducerName?: string;
  updatedByProducerId?: number;
  policies: Policy[];
  payments?: Payment[];
  totalFinancingAmount: number;
  expiresAt?: string;        // KAN-43 offer expiry
  isExpired?: boolean;       // KAN-43 computed: open offer past expiry
}

export interface Payment {
  id: number;
  quoteId: number;
  paymentDate: Date;
  stripePaymentId: string;
  status: string;
  amount: number;
  netAmount: number;
  createdAt: Date;
  paymentType: PaymentType;
  stripeInvoiceUrl?: string;
}

export interface CreateQuoteRequest {
  agentName?: string;
  insuredName: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  customerNumber?: string;
  loanType: LoanType;
  programId: number;
  insuranceCompanyId?: number;
  policies: PolicyDto[];
}

export interface UpdateQuoteRequest {
  agentName?: string;
  insuredName: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  customerNumber?: string;
  loanType: LoanType;
  programId: number;
  insuranceCompanyId?: number;
  quoteStatus: QuoteStatus;
}

// Supporting interfaces
export interface FinanceProgram {
  id: number;
  name: string;
  rateTiers: RateTier[];
  description?: string;
}

export interface RateTier {
  id: number;
  minAmount: number;
  maxAmount?: number;
  rate: number;
  adminFee: number;
}

export interface InsuranceCompany {
  id: number;
  name: string;
  code?: string;
}

export enum PaymentMethodType {
  ACH = 0,
  CreditCard = 1
}

export interface QuoteListRequest {
  offset: number;
  pageSize: number;
  status?: QuoteStatus;
  producerId?: number;
}

export interface QuoteListResponse {
  quotes: Quote[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface QuoteStatusOption {
  value: QuoteStatus;
  displayName: string;
}

// Customer-specific interfaces
export interface CustomerQuoteResponse {
  id: number;
  insuredName: string;
  agentName: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  customerNumber?: string;
  loanType: LoanType;
  status: QuoteStatus;
  appliedRate: number;
  policies: CustomerPolicyResponse[];
  totalFinancingAmount: number;
  monthlyPayment: number;
  monthlyPaymentFee: number;
  totalMonthlyPayment: number;
  firstMonthlyPaymentDate: Date;
  nextScheduledPaymentDate?: Date;
  payments: CustomerPaymentResponse[];
  paymentMethod?: CustomerPaymentMethodResponse;
  hasActiveSubscription: boolean;
  isAutopay?: boolean | null;
  createdAt: Date;
  adminFeeAmount?: number;
  adminFeeInstallments?: number[];
  manualInvoiceDaysUntilDue?: number;
}

export interface CustomerPolicyResponse {
  policyNumber: string;
  coverage: Coverage;
  effectiveDate: Date;
  expirationDate: Date;
  premiumAmount: number;
  taxesAmount: number;
  feesAmount: number;
  totalPremium: number;
  downAmount: number;
  cancelDays: CancelDays;
  auditable: boolean;
  lossPayeeRequired: boolean;
  lossPayeeRequest: boolean;
}

export interface CustomerPaymentResponse {
  id: number;
  paymentDate: Date;
  stripePaymentId: string;
  status: string;
  amount: number;
  netAmount: number;
  createdAt: Date;
  paymentType: PaymentType;
  stripeInvoiceUrl?: string;
}

export interface CustomerPaymentMethodResponse {
  type: string;
  brand?: string;
  last4?: string;
  bankName?: string;
  accountType?: string;
  routingNumber?: string;
  accountHolderType?: string;
  expMonth?: number;
  expYear?: number;
}

// Stripe subscription interfaces
export interface CreateSubscriptionRequest {
  quoteId: number;
  paymentMethodType: PaymentMethodType;
  autopay: boolean;
  paymentMethodId?: string;
  customerToken?: string;
  currency?: string;
}

export interface CreateSubscriptionResponse {
  quoteId: number;
  subscriptionId: string;
  clientSecret: string;
  invoiceId?: string | null;
  status: string;
  message: string;
}

export interface ConfirmSubscriptionPaymentRequest {
  quoteId: number;
  subscriptionId: string;
  invoiceId: string;
  paymentIntentId: string;
  customerToken: string;
}

export interface ConfirmSubscriptionPaymentResponse {
  quoteId: number;
  subscriptionId: string;
  message: string;
}

export interface SubscriptionStatusResponse {
  quoteId: number;
  subscriptionId: string;
  status: string;
  currentPeriodEnd?: Date;
  isActive: boolean;
}
